/**
 * apple-login-v1 — the OAuth error chip's AUXILIARY clause.
 *
 * `mapOAuthError` (contexts/AuthContext) returns the chip's PRIMARY copy;
 * this returns the recovery hint that follows it. Both landing surfaces
 * render the same chip, and until this lane each kept its own private copy
 * of the lookup — fine while it was a three-line match on one string.
 *
 * It is no longer one string. Two of the three primaries — "Sign-in
 * cancelled." and "Sign-in failed." — are deliberately provider-NEUTRAL, so
 * the clause has to be chosen on (primary × provider), and getting that pair
 * wrong is how an Apple failure ends up telling an athlete to "pick another
 * Google account". A two-dimensional lookup duplicated across two
 * release-blocking surfaces is a drift waiting to happen, so it lives here
 * once. The primaries are matched by VALUE rather than by a code because
 * that is the contract `mapOAuthError` already exposes.
 */

import type { OAuthProvider } from '../contexts/AuthContext';

/**
 * apple-signin-spinner — two new chip primaries, both native-shell-only.
 * They live HERE (not in AuthContext) so this module keeps zero runtime
 * dependency on the context — surfaces and tests mock AuthContext freely
 * without stranding the copy. AuthContext imports these; the reverse import
 * above is type-only and erased.
 *
 * ✅ RATIFIED AS SHIPPED — CD 2026-08-02, ruling 13 / §6
 * (`.claude/references/design/cd-2026-08-02/CD-RULINGS-2026-08-02.md`).
 * All three Apple strings sit in the chip family's grammar (short, sentence
 * case, subject-verb, full stop) beside `Sign-in cancelled.` and `Couldn't reach
 * Apple.`, and they pass the AD-2.1 set. `in this version` is correct: the cause
 * is the BUILD, not the hardware, and "version" implies a later one differs —
 * pairing it with `on this device` is not a mismatch but a sequence (why, then
 * where to go now). ⛔ No longer provisional. Do not re-open them.
 *
 * ⚠️ S8, and it is why this comment was REPLACED rather than deleted: a string
 * shipped provisionally under a release blocker is ratified or replaced at the
 * NEXT CD session — it does not sit provisional indefinitely — and its marker
 * comes off in the same PR. A stale "flagged for sign-off" comment tells the
 * next reader a law is up for grabs.
 */
export const OAUTH_APPLE_TIMEOUT_PRIMARY = "Apple sign-in didn't respond.";
export const OAUTH_APPLE_UNAVAILABLE_PRIMARY =
  "Apple sign-in isn't available in this version.";

export const CHIP_AUX_CANCELLED_GOOGLE = 'Try again or pick another Google account.';
/** Apple's sheet has no Google-style account chooser, but the athlete can
 *  switch Apple ID or change the relay choice — so the clause stays true
 *  without naming a picker that does not exist. */
export const CHIP_AUX_CANCELLED_APPLE = 'Try again or pick another account.';
export const CHIP_AUX_NETWORK = 'Check your connection and try again.';
/**
 * B251 — RATIFIED by the founder 2026-09-24 ("we need to say something more
 * than just try again"), put in the register by the coordinator. The prior
 * sentence carried an em dash, and the lane's interim proposal promised
 * details the product never surfaces. Sentence case, full stop, and it names
 * an action that can succeed.
 */
export const CHIP_AUX_GENERIC = 'Sorry, there was a problem signing you in. Please try again.';
/** The one chip where "try again" would be a lie: retrying a build with no
 *  plugin fails identically. Name the working door instead.
 *
 *  ⚖️ RATIFIED AS A RULE (CD 2026-08-02, §6): A RECOVERY CLAUSE MUST NAME AN
 *  ACTION THAT CAN SUCCEED. This is no longer just this chip's reasoning — it
 *  binds every recovery clause in the product. */
export const CHIP_AUX_APPLE_UNAVAILABLE = 'Use Google to sign in on this device.';

/**
 * B178 fix-2 — decision X's ratified pair (founder, 2026-09-16), the chip for
 * a sign-in tap that never got a client to sign in with: the tap-level wait
 * hit its cap, or the boot exhausted both its cycles first. Neither is an
 * oauth failure — nothing was ever attempted — so it is not one of
 * `mapOAuthError`'s buckets. ONE clause for both web and the shell (the
 * brief proposed a web/shell split; the founder replaced it with this pair).
 */
export const OAUTH_NOT_READY_PRIMARY = 'Sign-in is not ready yet.';
export const CHIP_AUX_NOT_READY = 'Check your connection and try again.';

/**
 * B207/R14 — the native `ASWebAuthenticationSession` plugin's own
 * unavailable-chip pair (spec-signin-auth-session.md §4.4), mirroring the
 * Apple pair's shape exactly: short, sentence case, subject-verb, full stop.
 *
 * ⚠️ PROVISIONAL — flagged for a founder/Claude Design sign-off before Build
 * 33 ships with it live, per the exact precedent this file's own Apple
 * strings set (CD ruling 2026-08-02, ruling 13/§6, "RATIFIED AS SHIPPED"
 * above). See spec-signin-auth-session.md acceptance criterion 18 and Open
 * Question 5. Do not remove this marker except at that sign-off.
 */
export const OAUTH_NATIVE_UNAVAILABLE_PRIMARY = "Sign-in isn't available in this version.";
/** The other working door, when the native plugin behind the TAPPED provider
 *  is missing — the same "name the working door" rule `CHIP_AUX_APPLE_UNAVAILABLE`
 *  already follows (CD 2026-08-02, §6). Google's own degraded path is not
 *  currently reachable in live code (spec §4.3), but the copy costs nothing
 *  to have ready if that path is ever re-wired. */
export const CHIP_AUX_NATIVE_UNAVAILABLE_GOOGLE = 'Use Apple to sign in on this device.';
export const CHIP_AUX_NATIVE_UNAVAILABLE_APPLE = 'Use Google to sign in on this device.';

/**
 * The clause that follows `primary` in the chip.
 *
 * `provider` is what `AuthContext.oauthErrorProvider` reports. It is optional
 * and defaults to 'google' so a surface that has not been taught about the
 * second provider renders exactly today's copy rather than something new.
 */
export function oauthAuxClause(
  primary: string,
  provider: OAuthProvider | null = 'google',
): string {
  if (primary === OAUTH_NOT_READY_PRIMARY) {
    return CHIP_AUX_NOT_READY;
  }
  if (primary === 'Sign-in cancelled.') {
    return provider === 'apple' ? CHIP_AUX_CANCELLED_APPLE : CHIP_AUX_CANCELLED_GOOGLE;
  }
  if (primary === "Couldn't reach Google." || primary === "Couldn't reach Apple.") {
    return CHIP_AUX_NETWORK;
  }
  if (primary === OAUTH_APPLE_UNAVAILABLE_PRIMARY) {
    return CHIP_AUX_APPLE_UNAVAILABLE;
  }
  if (primary === OAUTH_NATIVE_UNAVAILABLE_PRIMARY) {
    return provider === 'apple' ? CHIP_AUX_NATIVE_UNAVAILABLE_APPLE : CHIP_AUX_NATIVE_UNAVAILABLE_GOOGLE;
  }
  // OAUTH_APPLE_TIMEOUT_PRIMARY deliberately falls through: a wedged bridge
  // call is retryable, and the generic clause already says exactly that.
  return CHIP_AUX_GENERIC;
}
